import { TFunction } from 'i18next';
import { format, Locale } from 'date-fns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { PassOfferSummary, PassTargetDetail, PassTargetType, PassUnitType, PassValidityStartPolicy } from '../models/Pass';
import type { TutorSessionType } from '../models/Tutor';
import { normalizeTutorSessionType } from './tutorSessionType';

const DATE_LOCALES: { [key: string]: Locale } = { en: enGB, pl: plPL };

/** API stores ROOM entitlement as minutes; UI shows hours everywhere. */
export function minutesToDisplayHours(minutes: number): number {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? hours : Math.round(hours * 100) / 100;
}

/** e.g. "Jul 30, 2026" — shared with UserPassesPage/UserPassDetailsPage formatting. */
export function formatPassValidUntil(dateString: string | null | undefined, language: string): string | null {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    const locale = DATE_LOCALES[language] || enGB;
    return format(date, 'PP', { locale });
  } catch {
    return new Date(dateString).toLocaleDateString(language);
  }
}

/** Whole calendar days from now until `dateString` (negative if past). Null when unparseable/missing. */
export function passDaysRemaining(dateString: string | null | undefined): number | null {
  if (!dateString) return null;
  const end = new Date(dateString);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((startOfEnd.getTime() - startOfToday.getTime()) / (24 * 60 * 60 * 1000));
}

/** e.g. "10 lessons" for COUNT offers, "4 hours" for MINUTE offers. */
export function passEntitlementLabel(
  offer: Pick<PassOfferSummary, 'quantity' | 'unitType'>,
  t: TFunction,
): string {
  if (offer.unitType === 'MINUTE') {
    return t('passes.entitlementHours', '{{count}} hours', {
      count: minutesToDisplayHours(offer.quantity),
    });
  }
  return t('passes.entitlementLessons', '{{count}} lessons', { count: offer.quantity });
}

export function formatPassPrice(
  offer: Pick<PassOfferSummary, 'fixedPrice' | 'currencyCode'>,
  language: string,
): string {
  return formatPassMoney(offer.fixedPrice, offer.currencyCode || 'PLN', language);
}

/** Wallet purchase amount (or any amount + currency). */
export function formatPassMoney(amount: number, currencyCode: string, language: string): string {
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currencyCode || 'PLN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** e.g. "3 / 10 lessons" or "0.75 / 2 hours" — used of total for wallet/instance balances. */
export function passQuantityLabel(
  used: number,
  total: number,
  unitType: PassUnitType,
  t: TFunction,
): string {
  if (unitType === 'MINUTE') {
    return t('userPasses.usedHours', '{{used}} / {{total}} hours', {
      used: minutesToDisplayHours(used),
      total: minutesToDisplayHours(total),
    });
  }
  return t('userPasses.usedLessons', '{{used}} / {{total}} lessons', { used, total });
}

/** e.g. "3 / 10 lessons" remaining — for booking tender where spendable balance matters. */
export function passRemainingQuantityLabel(
  remaining: number,
  total: number,
  unitType: PassUnitType,
  t: TFunction,
): string {
  if (unitType === 'MINUTE') {
    return t('userPasses.remainingHours', '{{remaining}} / {{total}} hours', {
      remaining: minutesToDisplayHours(remaining),
      total: minutesToDisplayHours(total),
    });
  }
  return t('userPasses.remainingLessons', '{{remaining}} / {{total}} lessons', { remaining, total });
}

/** Quantity already spent/committed (total − remaining). */
export function passUsedQuantity(remaining: number, total: number): number {
  return Math.max(0, total - remaining);
}

/** Signed usage-event delta; MINUTE passes shown in hours. */
export function passQuantityDeltaLabel(delta: number, unitType: PassUnitType): string {
  const value = unitType === 'MINUTE' ? minutesToDisplayHours(delta) : delta;
  return value > 0 ? `+${value}` : String(value);
}

export function passTargetTypeLabel(targetType: PassTargetType, t: TFunction): string {
  return targetType === 'ROOM'
    ? t('passes.targetTypeSpace', 'Rehearsal room')
    : t('passes.targetTypeLessonOption', 'Tutor lesson');
}

/** Short user-facing label for when the validity clock starts. */
export function passValidityStartPolicyLabel(
  policy: PassValidityStartPolicy | null | undefined,
  t: TFunction,
): string | null {
  if (policy === 'FROM_FIRST_USE') {
    return t('userPasses.validityStartPolicyFromFirstUse', 'From first use');
  }
  if (policy === 'FROM_PURCHASE' || policy === 'FROM_ACTIVATION') {
    return t('userPasses.validityStartPolicyFromPurchase', 'From purchase');
  }
  return null;
}

/** Short chip label: "Rehearsal" / "Tutor". */
export function passTargetTypeShortLabel(targetType: PassTargetType, t: TFunction): string {
  return targetType === 'ROOM'
    ? t('passes.targetTypeSpaceShort', 'Rehearsal')
    : t('passes.targetTypeLessonOptionShort', 'Tutor');
}

/** Prefer embedded `offer.targets`; fall back to an id→name map (e.g. page-local rooms). */
export function resolvePassOfferTargetNames(
  offer: Pick<PassOfferSummary, 'targetIds' | 'targets'>,
  targetNameByTargetId?: Record<string, string | null | undefined>,
): (string | null)[] {
  const byId: Record<string, string | null | undefined> = { ...(targetNameByTargetId ?? {}) };
  for (const target of offer.targets ?? []) {
    const name = target.name?.trim();
    if (target.id && name) byId[target.id] = name;
  }
  return (offer.targetIds ?? []).map((id) => byId[id]?.trim() || null);
}

/** Session type from embedded targets (first known), else from an id→sessionType map. Room passes have no lesson place. */
export function resolvePassOfferSessionType(
  offer: Pick<PassOfferSummary, 'targetType' | 'targetIds' | 'targets'>,
  sessionTypeByTargetId?: Record<string, TutorSessionType | null | undefined>,
): TutorSessionType | null {
  if (offer.targetType === 'ROOM') {
    return null;
  }
  for (const target of offer.targets ?? []) {
    if (target.sessionType) {
      const normalized = normalizeTutorSessionType(target.sessionType);
      if (normalized) return normalized;
    }
  }
  for (const targetId of offer.targetIds ?? []) {
    const sessionType = sessionTypeByTargetId?.[targetId];
    const normalized = normalizeTutorSessionType(sessionType);
    if (normalized) return normalized;
  }
  return null;
}

/** First embedded target detail for an id, when present. */
export function passOfferTargetById(
  offer: Pick<PassOfferSummary, 'targets'>,
  targetId: string,
): PassTargetDetail | undefined {
  return (offer.targets ?? []).find((target) => target.id === targetId);
}
